import { ActionBarContainer } from "$components/action-bar/action-bar-container";
import { useRegisterActionBar } from "$components/action-bar/action-bar-offset";
import { ItemPriceOwner } from "elephant-web/gql";
import { useRef } from "react";
import { PriceListDefaultModeBar } from "./price-list-default-mode-bar";
import { PriceListEditModeBar } from "./price-list-edit-mode-bar";
import { PriceListReviewModeBar } from "./price-list-review-mode-bar";

export type EditModeState = {
  active: boolean;
  changedCount: number;
  selectedCount: number;
  onApplyAdjustment: (percentageDelta: number) => void;
  onSave: () => void;
  onCancel: () => void;
  submitting: boolean;
};

export type ReviewModeState = {
  active: boolean;
  onAcknowledge: () => void;
  onGoBack: () => void;
  acknowledging: boolean;
};

type Props = {
  priceListId: string;
  priceListOwner: ItemPriceOwner;
  isConnectedToOrg: boolean;
  refetchPriceList: () => void;
  className?: string;
  editMode: EditModeState;
  reviewMode: ReviewModeState;
};

export const PriceListActionBar: FC<Props> = ({
  priceListId,
  priceListOwner,
  isConnectedToOrg,
  refetchPriceList,
  className,
  editMode,
  reviewMode,
}) => {
  // One registration covers whichever variant is on screen, so the app reserves
  // space for (and lifts overlays above) the bar regardless of mode.
  const actionBarRef = useRef<HTMLElement>(null);
  useRegisterActionBar(actionBarRef);

  // The bar's contents are swapped out by mode. The default mode is a list of
  // actions, so it goes through the config-driven <ActionBar> (which provides
  // its own fixed container). The edit/review modes are bespoke interactions,
  // so they render their own contents into the shared <ActionBarContainer> —
  // the exported escape hatch that mimics the bar without the action-list API.
  if (reviewMode.active) {
    return (
      <ActionBarContainer ref={actionBarRef} className={className}>
        <PriceListReviewModeBar
          onAcknowledge={reviewMode.onAcknowledge}
          onGoBack={reviewMode.onGoBack}
          acknowledging={reviewMode.acknowledging}
        />
      </ActionBarContainer>
    );
  }

  if (editMode.active) {
    return (
      <ActionBarContainer ref={actionBarRef} className={className}>
        <PriceListEditModeBar
          changedCount={editMode.changedCount}
          selectedCount={editMode.selectedCount}
          onApplyAdjustment={editMode.onApplyAdjustment}
          onSave={editMode.onSave}
          onCancel={editMode.onCancel}
          submitting={editMode.submitting}
        />
      </ActionBarContainer>
    );
  }

  return (
    <PriceListDefaultModeBar
      className={className}
      containerRef={actionBarRef}
      priceListId={priceListId}
      priceListOwner={priceListOwner}
      isConnectedToOrg={isConnectedToOrg}
      refetchPriceList={refetchPriceList}
    />
  );
};
